import React, { CSSProperties } from 'react';
import { Button, makeStyles, Theme, IconButton, Icon } from '@material-ui/core';
import clsx from 'clsx';
import { useModeFormStyle, ModeFormInputSection } from '@moderepo/mode-app-components';
import { DeviceConfigParamSet } from '@moderepo/mode-internal-apis';
import { DeviceConfigParamSetComp } from './DeviceConfigParamSetComp';
import { DeviceConfigInputErrors, DeviceConfigInputs } from './utils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        paramSetInputsContainer: {
            border  : 'none',
            padding : 0,
            margin  : 0,
            position: 'relative',

            '&.repeatable': {
                padding     : 'revert',
                margin      : 'revert',
                border      : `1px solid ${theme.palette.divider}`,
                borderRadius: theme.shape.borderRadius,
                marginBottom: theme.spacing(2),
            },
        },

        addParamSetActions: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
        },

        emptyParamSetConfigMessage: {
            color       : theme.palette.text.secondary,
            marginBottom: theme.spacing(2),
        },

        removeParamSetActions: {
            position : 'absolute',
            top      : 0,
            right    : 0,
            zIndex   : 1,
            transform: 'translate3d(50%, -50%, 0)',
        },

        paramSetError: {
            color    : theme.palette.error.main,
            textAlign: 'center',
            padding  : theme.spacing(1),
        },
    };
}, {
    name: 'DeviceConfigForm', index: 1,
});



export interface DeviceConfigFormProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly isEditing?: boolean | undefined;
    readonly deviceId: number;
    readonly deviceConfigSchema: readonly DeviceConfigParamSet[];
    readonly deviceConfigInputs: DeviceConfigInputs;
    readonly inputErrors?: DeviceConfigInputErrors | undefined;

    readonly texts: {
        readonly emptyRepeatableParamSetInputsMessage: string;
        readonly add: string;
    }

    readonly onFieldClicked?: (fieldName: string)=> void;
    readonly onAddParamInstanceClicks: (paramSetIndex: number)=> void;
    readonly onRemoveParamInstanceClicks: (paramSetIndex: number, paramSetInstanceIndex: number)=> void;
    readonly onInputChange: (inputName: string, value: any, paramSetIndex: number, paramSetInstanceIndex: number)=> void;
}



export const DeviceConfigForm: React.FC<DeviceConfigFormProps> = (props: DeviceConfigFormProps) => {

    const formClasses = useModeFormStyle();
    const classes = useStyle();

    return (
        <div
            className={clsx(props.className, classes.root)}
            style={props.style}
        >
            {/*
                deviceConfigSchema is an Array of parameter set. For each set, we will create DeviceConfigParamSetComp
                to provide the UI.
            */}
            {props.deviceConfigSchema.map((paramSet, paramSetIndex) => {

                // The inputs object/storage for each paramSet is an ARRAY of paramSet inputs instances, even for paramSets that are not
                // repeatable. This way it will be easier to loop though the array and create DeviceConfigParamSetComp
                // for each paramSet inputs instance.
                const paramSetInputsInstances = props.deviceConfigInputs[paramSetIndex];

                // Get the errors for all the paramSet instances. If 1 of them contain error, we will highlight the section
                // title bar to indicate one of the inputs in that section contains error
                const paramSetInputErrors = props.inputErrors?.[paramSetIndex];
                const hasError = paramSetInputErrors?.find((paramSetInstance) => {
                    return Object.values(paramSetInstance).find((value) => {
                        return value !== undefined;
                    }) !== undefined;
                }) !== undefined;

                return (
                    <ModeFormInputSection
                        key={`${paramSet.name}-${paramSet.keyName}`}
                        className={clsx(hasError && 'error')}
                        collapsible
                        collapsed
                        title={paramSet.name}
                        icon="format_list_bulleted"
                    >
                        {paramSetInputsInstances?.map((paramSetInput, instanceIndex) => {

                            const paramSetInstanceInputErrors = paramSetInputErrors?.[instanceIndex];

                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <div key={`${paramSet.name}-${instanceIndex}`} className={clsx(formClasses.formInputGroup)}>
                                    <fieldset className={clsx(classes.paramSetInputsContainer, paramSet.repeatable && 'repeatable')}>
                                        <DeviceConfigParamSetComp
                                            deviceId={props.deviceId}
                                            parameterSet={paramSet}
                                            inputValues={paramSetInput}
                                            inputErrors={paramSetInstanceInputErrors}
                                            onFieldClicked={props.onFieldClicked}
                                            onInputChange={(fieldName: string, value: any) => {
                                                props.onInputChange(fieldName, value, paramSetIndex, instanceIndex);
                                            }}
                                            isEditing={props.isEditing}
                                        />
                                        {paramSetInstanceInputErrors?.[paramSet.name] && (
                                            <div className={classes.paramSetError}>{paramSetInstanceInputErrors[paramSet.name]}</div>
                                        )}

                                        {/* Add a Remove button to remove this paramSet instance if the paramSet is repeatable */}
                                        {paramSet.repeatable && props.isEditing && (
                                            <IconButton
                                                className={classes.removeParamSetActions}
                                                color="secondary"
                                                onClick={() => {
                                                    props.onRemoveParamInstanceClicks(paramSetIndex, instanceIndex);
                                                }}
                                            >
                                                <Icon>cancel</Icon>
                                            </IconButton>
                                        )}
                                    </fieldset>
                                </div>
                            );
                        })}

                        {/* Add a button for the user to add new set of params input if the paramSet is repeatable */}
                        {paramSet.repeatable && (
                            <div className={classes.addParamSetActions}>
                                {/* Show a message that there isn't any config created for the paramSet */}
                                {paramSetInputsInstances && paramSetInputsInstances.length === 0 && (
                                    <div className={classes.emptyParamSetConfigMessage}>
                                        {props.texts.emptyRepeatableParamSetInputsMessage}
                                    </div>
                                )}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => {
                                        props.onAddParamInstanceClicks(paramSetIndex);
                                    }}
                                >
                                    {props.texts.add}
                                </Button>
                            </div>
                        )}
                    </ModeFormInputSection>
                );
            })}
        </div>
    );
};
